import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  Users,
  CreditCard,
  Activity,
  Mail,
  Globe,
  Phone,
  MapPin,
  Calendar,
  AlertTriangle,
  Shield,
  KeyRound,
  UserX,
  UserCog,
  Eye,
  Loader2,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { Switch } from '@/app/components/ui/switch';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/simple-toaster';
import {
  usePlatformOrganization,
  usePlatformOrgUsers,
  usePlatformOrgModules,
  usePlatformOrgSubscription,
  useUpdatePlatformOrganization,
  useResendPlatformAdminInvitation,
  useUpdatePlatformOrgModules,
  useSyncPlatformOrgModules,
  useUpdatePlatformOrgUser,
  useResetPlatformUserPassword,
  useImpersonateUser,
  useExtendTrial,
  useDeleteOrganization,
  useNukePlatformUser,
  usePlatformOrgActivity,
} from '@/hooks/use-platform';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { useVerifyPassword } from '@/hooks/use-admin';
import type { PlatformOrgUser, PlatformOrgModule, PlatformActivityEvent } from '@/lib/types';
import { handleApiError, setAuthFlag } from '@/lib/api-client';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

// ─── Impersonation helpers ──────────────────────────────────────────────────

const IMPERSONATION_KEY = 'h3gear_impersonation';

interface ImpersonationState {
  staffReturnPath: string;
  targetUserName: string;
  targetOrgName: string;
}

function storeImpersonationState(state: ImpersonationState) {
  sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
}

export function getImpersonationState(): ImpersonationState | null {
  const raw = sessionStorage.getItem(IMPERSONATION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return null;
  }
}

export function clearImpersonationState() {
  sessionStorage.removeItem(IMPERSONATION_KEY);
}

// ─── Overview Tab ───────────────────────────────────────────────────────────

function OverviewTab({ orgId }: { orgId: number }) {
  const orgTimeZone = useOrgTimeZone();
  const { data: org, isLoading: orgLoading } = usePlatformOrganization(orgId);
  const { data: modulesData, isLoading: modulesLoading } = usePlatformOrgModules(orgId);
  const updateOrganization = useUpdatePlatformOrganization(orgId);
  const resendAdminInvitation = useResendPlatformAdminInvitation(orgId);
  const updateModules = useUpdatePlatformOrgModules(orgId);
  const syncModules = useSyncPlatformOrgModules(orgId);
  const [demoConfirmOpen, setDemoConfirmOpen] = useState(false);
  const [pendingDemoValue, setPendingDemoValue] = useState(false);
  const [demoNameConfirmation, setDemoNameConfirmation] = useState('');

  const requestDemoChange = (isDemo: boolean) => {
    setPendingDemoValue(isDemo);
    setDemoNameConfirmation('');
    setDemoConfirmOpen(true);
  };

  const confirmDemoChange = () => {
    updateOrganization.mutate(
      {
        is_demo: pendingDemoValue,
        ...(pendingDemoValue
          ? { demo_confirmation: demoNameConfirmation }
          : {}),
      },
      {
        onSuccess: () => {
          toast.success(`Demo organization ${pendingDemoValue ? 'enabled' : 'disabled'}.`);
          setDemoConfirmOpen(false);
          setDemoNameConfirmation('');
        },
        onError: (error) => {
          toast.error(handleApiError(error).message);
        },
      }
    );
  };

  const handleSyncModules = () => {
    syncModules.mutate(undefined, {
      onSuccess: () => {
        toast.success('Modules synced from plan successfully');
      },
      onError: () => {
        toast.error('Failed to sync modules from plan');
      },
    });
  };

  const handleResendAdminInvitation = () => {
    resendAdminInvitation.mutate(undefined, {
      onSuccess: (result) => {
        if (result.invitation_email_dispatched) {
          toast.success('Administrator invitation queued.');
        } else {
          toast.warning('Invitation refreshed, but email delivery is not configured.');
        }
      },
      onError: () => {
        toast.error('Failed to resend administrator invitation');
      },
    });
  };

  const handleModuleToggle = (moduleKey: string, enabled: boolean) => {
    const current: Record<string, boolean> = {};
    if (modulesData?.modules) {
      for (const m of modulesData.modules) {
        current[m.key] = m.enabled;
      }
    }
    current[moduleKey] = enabled;
    updateModules.mutate(current, {
      onError: () => {
        toast.error(`Failed to update module "${moduleKey}"`);
      },
    });
  };

  if (orgLoading) {
    return (
      <div className="grid gap-6 md:grid-cols-2">
        <Card><CardContent className="pt-6 space-y-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-5 w-full" />)}</CardContent></Card>
        <Card><CardContent className="pt-6 space-y-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</CardContent></Card>
      </div>
    );
  }

  if (!org) return null;

  // Group modules by category
  const modulesByCategory: Record<string, PlatformOrgModule[]> = {};
  if (modulesData?.modules) {
    for (const m of modulesData.modules) {
      const cat = m.category || 'Other';
      if (!modulesByCategory[cat]) modulesByCategory[cat] = [];
      modulesByCategory[cat].push(m);
    }
  }

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* Organization Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4" />
            Organization Info
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <MapPin className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Address</p>
              <p className="text-sm">{org.address || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Phone</p>
              <p className="text-sm">{org.phone || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Mail className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="text-sm">{org.email || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Globe className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Website</p>
              <p className="text-sm">{org.website || '—'}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Calendar className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Created</p>
              <p className="text-sm">{formatDateTimeInTimeZone(org.created_at, orgTimeZone)}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Shield className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
            <div>
              <p className="text-xs text-gray-500">Admin Contact</p>
              <p className="text-sm">{org.admin_email || '—'}</p>
            </div>
          </div>
          {org.admin_invitation && (
            <div className="border-t pt-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Mail className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-500">Admin Invitation</p>
                      <Badge
                        variant="secondary"
                        className={
                          org.admin_invitation.status === 'accepted'
                            ? 'bg-green-100 text-green-800 capitalize'
                            : org.admin_invitation.status === 'expired'
                              ? 'bg-red-100 text-red-800 capitalize'
                              : 'bg-amber-100 text-amber-800 capitalize'
                        }
                      >
                        {org.admin_invitation.status}
                      </Badge>
                    </div>
                    <p className="text-sm">{org.admin_invitation.email}</p>
                    {org.admin_invitation.status !== 'accepted' && org.admin_invitation.expires_at && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        Expires{' '}
                        {formatDateTimeInTimeZone(
                          org.admin_invitation.expires_at,
                          orgTimeZone
                        )}
                      </p>
                    )}
                  </div>
                </div>
                {org.admin_invitation.status !== 'accepted' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResendAdminInvitation}
                    disabled={resendAdminInvitation.isPending}
                  >
                    {resendAdminInvitation.isPending && (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    )}
                    Resend
                  </Button>
                )}
              </div>
            </div>
          )}
          {org.industry && (
            <div className="flex items-start gap-3">
              <Building2 className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Industry</p>
                <p className="text-sm">{org.industry}</p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 border-t pt-3">
            <div>
              <Label htmlFor="demo-organization" className="text-sm font-medium">
                Demo org
              </Label>
              <p className="text-xs text-gray-500">Use in-app delivery for demo organization messaging.</p>
            </div>
            <Switch
              id="demo-organization"
              checked={org.is_demo}
              onCheckedChange={requestDemoChange}
              disabled={updateOrganization.isPending}
            />
          </div>
        </CardContent>
      </Card>

      {/* Module Toggles */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-base">
            Module Toggles
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncModules}
              disabled={syncModules.isPending || modulesLoading}
            >
              {syncModules.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              )}
              Sync Modules
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {modulesLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
            </div>
          ) : !modulesData?.modules?.length ? (
            <p className="text-sm text-gray-500">No modules configured</p>
          ) : (
            <div className="space-y-5">
              {Object.entries(modulesByCategory).map(([category, modules]) => (
                <div key={category}>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">{category}</p>
                  <div className="space-y-3">
                    {modules.map((mod) => (
                      <div key={mod.key} className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">{mod.name}</p>
                          {mod.description && (
                            <p className="text-xs text-gray-500">{mod.description}</p>
                          )}
                        </div>
                        <Switch
                          checked={mod.enabled}
                          onCheckedChange={(checked) => handleModuleToggle(mod.key, checked)}
                          disabled={updateModules.isPending}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={demoConfirmOpen} onOpenChange={setDemoConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDemoValue ? 'Enable' : 'Disable'} Demo org?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Demo orgs never receive scheduled reminders or digests, and JSA signature emails are in-app only.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingDemoValue && (
            <div className="space-y-2">
              <Label htmlFor="demo-organization-confirmation">
                Type <strong>{org.name}</strong> to confirm
              </Label>
              <Input
                id="demo-organization-confirmation"
                value={demoNameConfirmation}
                onChange={(event) => setDemoNameConfirmation(event.target.value)}
                autoComplete="off"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateOrganization.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                confirmDemoChange();
              }}
              disabled={
                updateOrganization.isPending ||
                (pendingDemoValue && demoNameConfirmation !== org.name)
              }
            >
              {updateOrganization.isPending ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Updating...</>
              ) : (
                'Confirm'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Users Tab ──────────────────────────────────────────────────────────────

function UsersTab({ orgId, orgName }: { orgId: number; orgName: string }) {
  const orgTimeZone = useOrgTimeZone();
  const { data: usersData, isLoading } = usePlatformOrgUsers(orgId);
  const updateUser = useUpdatePlatformOrgUser(orgId);
  const resetPassword = useResetPlatformUserPassword(orgId);
  const impersonate = useImpersonateUser(orgId);
  const verifyPassword = useVerifyPassword();
  const [impersonateTarget, setImpersonateTarget] = useState<PlatformOrgUser | null>(null);
  const [staffPassword, setStaffPassword] = useState('');
  const [impersonateError, setImpersonateError] = useState<string | null>(null);
  const impersonatePending = verifyPassword.isPending || impersonate.isPending;
  const nukeUser = useNukePlatformUser(orgId);
  const navigate = useNavigate();

  // Reset password modal state
  const [resetTarget, setResetTarget] = useState<PlatformOrgUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetOpen, setResetOpen] = useState(false);

  // Deactivate confirm state
  const [deactivateTarget, setDeactivateTarget] = useState<PlatformOrgUser | null>(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const reactivatingAdmin =
    !!deactivateTarget &&
    !deactivateTarget.is_active &&
    deactivateTarget.role === 'Admin';

  // Role change state
  const [roleTarget, setRoleTarget] = useState<PlatformOrgUser | null>(null);
  const [roleValue, setRoleValue] = useState('');
  const [roleOpen, setRoleOpen] = useState(false);
  const [adminPromotionOpen, setAdminPromotionOpen] = useState(false);

  // Nuke (hard-delete) confirm state
  const [nukeTarget, setNukeTarget] = useState<PlatformOrgUser | null>(null);
  const [nukeOpen, setNukeOpen] = useState(false);

  const handleResetPassword = () => {
    if (!resetTarget || !newPassword.trim()) return;
    resetPassword.mutate(
      { userId: resetTarget.id, new_password: newPassword },
      {
        onSuccess: () => {
          toast.success(`Password for ${resetTarget.username} has been reset.`);
          setResetOpen(false);
          setNewPassword('');
          setResetTarget(null);
        },
        onError: () => {
          toast.error('Failed to reset password');
        },
      }
    );
  };

  const handleDeactivate = () => {
    if (!deactivateTarget) return;
    updateUser.mutate(
      {
        userId: deactivateTarget.id,
        is_active: !deactivateTarget.is_active,
        ...(reactivatingAdmin ? { confirm_admin_grant: true } : {}),
      },
      {
        onSuccess: () => {
          toast.success(`${deactivateTarget.username} has been ${deactivateTarget.is_active ? 'deactivated' : 'activated'}.`);
          setDeactivateOpen(false);
          setDeactivateTarget(null);
        },
        onError: () => {
          toast.error('Failed to update user');
        },
      }
    );
  };

  const handleRoleChange = (confirmAdminGrant = false) => {
    if (!roleTarget || !roleValue) return;
    if (
      roleValue === 'Admin' &&
      roleTarget.role !== 'Admin' &&
      !confirmAdminGrant
    ) {
      setAdminPromotionOpen(true);
      return;
    }
    updateUser.mutate(
      {
        userId: roleTarget.id,
        role: roleValue,
        ...(confirmAdminGrant ? { confirm_admin_grant: true } : {}),
      },
      {
        onSuccess: () => {
          toast.success(`${roleTarget.username} role changed to ${roleValue}.`);
          setRoleOpen(false);
          setRoleTarget(null);
          setRoleValue('');
        },
        onError: () => {
          toast.error('Failed to update role');
        },
      }
    );
  };

  const handleNuke = () => {
    if (!nukeTarget) return;
    nukeUser.mutate(nukeTarget.id, {
      onSuccess: () => {
        toast.success(`${nukeTarget.username} has been permanently deleted.`);
        setNukeOpen(false);
        setNukeTarget(null);
      },
      onError: () => {
        toast.error('Failed to delete user');
      },
    });
  };

  const handleImpersonate = async () => {
    if (!impersonateTarget || !staffPassword || impersonatePending) return;
    const user = impersonateTarget;
    setImpersonateError(null);
    try {
      const verification = await verifyPassword.mutateAsync({ password: staffPassword });
      setStaffPassword('');
      if (!verification.valid || !verification.step_up_token) {
        setImpersonateError('Current password is incorrect.');
        return;
      }
      await impersonate.mutateAsync({ userId: user.id, step_up_token: verification.step_up_token });
      // Store impersonation state so the banner can restore
      storeImpersonationState({
        staffReturnPath: window.location.pathname,
        targetUserName: `${user.first_name} ${user.last_name}`.trim() || user.username,
        targetOrgName: orgName,
      });
      // Tokens arrive only as httpOnly cookies. The session lasts 15 minutes
      // and cannot be refreshed.
      setAuthFlag();
      // Navigate to the main app as the impersonated user
      navigate('/dashboard');
      // Force a full reload so AuthProvider picks up the new cookies
      window.location.href = '/dashboard';
    } catch (err: unknown) {
      setStaffPassword('');
      setImpersonateError(handleApiError(err as Error).message);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const users = usersData?.users ?? [];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" />
            Users ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">No users in this organization</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 text-left">
                    <th className="pb-3 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="pb-3 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="pb-3 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="pb-3 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="pb-3 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Login ({orgTimeZone})
                    </th>
                    <th className="pb-3 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Legal acceptance
                    </th>
                    <th className="pb-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 pr-4">
                        <p className="text-sm font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-xs text-gray-500">{user.username}</p>
                      </td>
                      <td className="py-3 pr-4 text-sm text-gray-600">{user.email}</td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline" className="text-xs capitalize">
                          {user.role}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge
                          variant={user.is_active ? 'default' : 'secondary'}
                          className={
                            user.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-100'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-100'
                          }
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 pr-4 text-sm text-gray-500">
                        {user.last_login
                          ? formatDateTimeInTimeZone(user.last_login, orgTimeZone)
                          : 'Never'}
                      </td>
                      <td className="py-3 pr-4 text-xs text-gray-600">
                        {user.legal_accepted_at ? (
                          <div className="space-y-0.5">
                            <p>Terms: {user.accepted_terms_version}</p>
                            <p>Privacy: {user.accepted_privacy_version}</p>
                            <p>
                              {formatDateTimeInTimeZone(
                                user.legal_accepted_at,
                                orgTimeZone,
                              )}
                            </p>
                          </div>
                        ) : (
                          'Not yet accepted'
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            title="Reset Password"
                            onClick={() => {
                              setResetTarget(user);
                              setNewPassword('');
                              setResetOpen(true);
                            }}
                          >
                            <KeyRound className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            title="Change Role"
                            onClick={() => {
                              setRoleTarget(user);
                              setRoleValue(user.role);
                              setRoleOpen(true);
                            }}
                          >
                            <UserCog className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            title={user.is_active ? 'Deactivate' : 'Activate'}
                            onClick={() => {
                              setDeactivateTarget(user);
                              setDeactivateOpen(true);
                            }}
                          >
                            <UserX className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            title="Impersonate"
                            onClick={() => {
                              setStaffPassword('');
                              setImpersonateError(null);
                              setImpersonateTarget(user);
                            }}
                            disabled={impersonatePending}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete User"
                            onClick={() => {
                              setNukeTarget(user);
                              setNukeOpen(true);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!impersonateTarget}
        onOpenChange={(open) => {
          if (!open && !impersonatePending) {
            setImpersonateTarget(null);
            setStaffPassword('');
            setImpersonateError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Password</DialogTitle>
            <DialogDescription>
              Re-enter your password to impersonate {impersonateTarget?.username}.
              The session lasts 15 minutes and cannot be refreshed.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(event) => { event.preventDefault(); void handleImpersonate(); }}>
            {impersonateError && <p role="alert" className="text-sm text-red-600">{impersonateError}</p>}
            <div className="space-y-2 py-4">
              <Label htmlFor="impersonate-password">Current Password</Label>
              <Input
                id="impersonate-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={staffPassword}
                onChange={(event) => setStaffPassword(event.target.value)}
                disabled={impersonatePending}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={impersonatePending} onClick={() => {
                setImpersonateTarget(null);
                setStaffPassword('');
                setImpersonateError(null);
              }}>Cancel</Button>
              <Button type="submit" disabled={!staffPassword || impersonatePending}>
                {impersonatePending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirm Impersonation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{resetTarget?.username}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={!newPassword.trim() || resetPassword.isPending}
            >
              {resetPassword.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Resetting...</>
              ) : (
                'Reset Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirm Dialog */}
      <AlertDialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deactivateTarget?.is_active ? 'Deactivate User' : 'Activate User'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateTarget?.is_active
                ? `Are you sure you want to deactivate ${deactivateTarget?.username}? They will no longer be able to log in.`
                : reactivatingAdmin
                  ? `Admins have full access to company settings, users, and every enabled module. Activate ${deactivateTarget?.username} only if they should have that access.`
                : `Are you sure you want to activate ${deactivateTarget?.username}? They will be able to log in again.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivate}>
              {deactivateTarget?.is_active ? 'Deactivate' : 'Activate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Nuke (Hard-Delete) Confirm Dialog */}
      <AlertDialog open={nukeOpen} onOpenChange={setNukeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Permanently</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete <strong>{nukeTarget?.username}</strong> ({nukeTarget?.email})?
              This will remove their TechProfile, module roles, and membership in this organization. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={nukeUser.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleNuke}
              disabled={nukeUser.isPending}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {nukeUser.isPending ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Deleting...</>
              ) : (
                'Delete Permanently'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Role Dialog */}
      <Dialog open={roleOpen} onOpenChange={setRoleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              Update role for <strong>{roleTarget?.username}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="role-select">Role</Label>
            <Select value={roleValue} onValueChange={setRoleValue}>
              <SelectTrigger id="role-select">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Admin">Admin</SelectItem>
                <SelectItem value="Manager">Manager</SelectItem>
                <SelectItem value="Supervisor">Supervisor</SelectItem>
                <SelectItem value="User">User</SelectItem>
                <SelectItem value="Viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleRoleChange()}
              disabled={!roleValue || roleValue === roleTarget?.role || updateUser.isPending}
            >
              {updateUser.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Saving...</>
              ) : (
                'Save Role'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={adminPromotionOpen}
        onOpenChange={setAdminPromotionOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promote user to Admin?</AlertDialogTitle>
            <AlertDialogDescription>
              Admins have full access to company settings, users, and every enabled module.
              Promote {roleTarget?.username} only if they should have that access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updateUser.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={updateUser.isPending}
              onClick={() => {
                setAdminPromotionOpen(false);
                handleRoleChange(true);
              }}
            >
              {updateUser.isPending ? 'Saving...' : 'Promote to Admin'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Subscription Tab ───────────────────────────────────────────────────────

function SubscriptionTab({ orgId }: { orgId: number }) {
  const orgTimeZone = useOrgTimeZone();
  const { data: sub, isLoading } = usePlatformOrgSubscription(orgId);
  const extendTrial = useExtendTrial();
  const [trialDays, setTrialDays] = useState('14');

  const handleExtendTrial = () => {
    const days = parseInt(trialDays, 10);
    if (isNaN(days) || days <= 0) return;
    extendTrial.mutate(
      { orgId, trial_days: days },
      {
        onSuccess: () => {
          toast.success(`Trial extended by ${days} days.`);
        },
        onError: () => {
          toast.error('Failed to extend trial');
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 space-y-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-5 w-full" />)}
        </CardContent>
      </Card>
    );
  }

  if (!sub) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-gray-500 text-center py-4">No subscription found</p>
        </CardContent>
      </Card>
    );
  }

  const statusColor: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    trialing: 'bg-blue-100 text-blue-800',
    past_due: 'bg-yellow-100 text-yellow-800',
    canceled: 'bg-red-100 text-red-800',
    incomplete: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CreditCard className="h-4 w-4" />
            Subscription Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Plan</span>
            <span className="text-sm font-medium">{sub.plan_name}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Status</span>
            <Badge className={statusColor[sub.status] ?? 'bg-gray-100 text-gray-600'}>
              {sub.status}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Billing Cycle</span>
            <span className="text-sm capitalize">{sub.billing_cycle || '—'}</span>
          </div>
          {sub.current_period_start && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Period Start</span>
              <span className="text-sm">
                {formatDateTimeInTimeZone(sub.current_period_start, orgTimeZone)}
              </span>
            </div>
          )}
          {sub.current_period_end && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Period End</span>
              <span className="text-sm">
                {formatDateTimeInTimeZone(sub.current_period_end, orgTimeZone)}
              </span>
            </div>
          )}
          {sub.trial_end && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Trial Ends</span>
              <span className="text-sm font-medium text-blue-600">
                {formatDateTimeInTimeZone(sub.trial_end, orgTimeZone)}
                {sub.trial_days_remaining > 0 && ` (${sub.trial_days_remaining}d left)`}
              </span>
            </div>
          )}
          {sub.stripe_subscription_id && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Stripe ID</span>
              <span className="text-xs font-mono text-gray-500">{sub.stripe_subscription_id}</span>
            </div>
          )}
          {sub.cancel_at_period_end && (
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              Cancels at period end
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="trial-days">Extend / Start Trial</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="trial-days"
                type="number"
                min={1}
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                className="w-24"
              />
              <Button
                onClick={handleExtendTrial}
                disabled={extendTrial.isPending}
                size="sm"
              >
                {extendTrial.isPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Extending...</>
                ) : (
                  'Extend Trial'
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Number of days to add to the trial period</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Activity Tab ───────────────────────────────────────────────────────────

function eventDescription(event: PlatformActivityEvent) {
  const accountActions: Record<string, string> = {
    invited: 'invited a user',
    joined: 'joined the organization',
    deactivated: 'deactivated a user account',
    module_toggled: 'toggled a module',
    settings_changed: 'changed organization settings',
    viewed: 'viewed organization activity',
  };
  const article = /^[aeio]/i.test(event.record_type) ? 'an' : 'a';
  return accountActions[event.action] ?? `${event.action} ${article} ${event.record_type}`;
}

export function ActivityTab({ orgId }: { orgId: number }) {
  const [page, setPage] = useState(1);
  const [module, setModule] = useState('all');
  const [user, setUser] = useState('all');
  const [showSystem, setShowSystem] = useState(false);
  const { data, isLoading, isError, refetch } = usePlatformOrgActivity(orgId, {
    page,
    module: module === 'all' ? undefined : module,
    user: user === 'all' ? undefined : user,
    show_system: showSystem,
  });

  return (
    <div className="space-y-6">
      <p className="flex items-center gap-2 text-sm text-gray-500">
        <Shield className="h-4 w-4 shrink-0" />
        Metadata only. Record contents are private. Access to this view is audited.
      </p>
      <p className="text-xs text-gray-500">Active users are counted from customer activity. For users with one organization, recent logins also count.</p>
      {isError && (
        <div role="alert" className="rounded-lg border border-red-200 p-4 text-sm">
          Unable to load activity. <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      )}
      {isLoading && <Skeleton className="h-32 w-full" />}
      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {(['7d', '30d'] as const).map((period) => (
              <Card key={period}>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Active users · last {period === '7d' ? '7' : '30'} days</CardTitle></CardHeader>
                <CardContent><p className="text-3xl font-bold">{data.summary.active_users[period]}</p></CardContent>
              </Card>
            ))}
          </div>
          <Card>
            <CardHeader><CardTitle className="text-base">Records created</CardTitle></CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead><tr className="border-b text-gray-500"><th className="pb-3">Module / record type</th><th className="pb-3 text-right">Last 7 days</th><th className="pb-3 text-right">Last 30 days</th></tr></thead>
                <tbody>
                  {data.summary.records_created.map((record) => (
                    <tr key={`${record.module}-${record.record_type}`} className="border-b last:border-0">
                      <td className="py-3">{data.modules[record.module]}: {record.record_type}</td>
                      <td className="py-3 text-right">{record.count_7d}</td><td className="py-3 text-right">{record.count_30d}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.summary.records_created.length === 0 && <p className="py-4 text-gray-500">No records created in the last 30 days.</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Last login per user</CardTitle></CardHeader>
            <CardContent>
              <p className="mb-3 text-xs text-gray-500">All times in {data.time_zone}. Active users have logged in or acted in the organization; platform staff are excluded.</p>
              <div className="max-h-56 overflow-auto">
                <table className="w-full text-left text-sm">
                  <thead><tr className="border-b text-gray-500"><th className="pb-3">Username</th><th className="pb-3">Last login</th></tr></thead>
                  <tbody>{data.summary.users.map((account) => (
                    <tr key={account.id} className="border-b last:border-0"><td className="py-2">{account.username}</td><td className="py-2">{account.last_login ? formatDateTimeInTimeZone(account.last_login, data.time_zone) : 'Never logged in'}</td></tr>
                  ))}</tbody>
                </table>
                {data.summary.users.length === 0 && <p className="py-4 text-gray-500">No users in this organization.</p>}
              </div>
            </CardContent>
          </Card>
        </>
      )}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Activity className="h-4 w-4" />Recent activity</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label htmlFor="activity-module">Module</Label>
              <Select value={module} onValueChange={(value) => { setModule(value); setPage(1); }}>
                <SelectTrigger id="activity-module" className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All modules</SelectItem>{Object.entries(data?.modules ?? {}).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="activity-user">User</Label>
              <Select value={user} onValueChange={(value) => { setUser(value); setPage(1); }}>
                <SelectTrigger id="activity-user" className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All users</SelectItem>{data?.users.map((account) => <SelectItem key={account.id} value={String(account.id)}>{account.username}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Switch id="activity-system" checked={showSystem} onCheckedChange={(checked) => { setShowSystem(checked); setPage(1); }} />
              <Label htmlFor="activity-system">Show system events</Label>
            </div>
          </div>
          {data && <p className="text-xs text-gray-500">Newest first · {data.time_zone}</p>}
          <ul className="divide-y">
            {data?.results.map((event) => (
              <li key={event.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                <span><span className="font-medium">{event.username}</span> {eventDescription(event)}</span>
                <span className="text-gray-500">— {formatDateTimeInTimeZone(event.timestamp, data.time_zone)}</span>
              </li>
            ))}
          </ul>
          {data?.results.length === 0 && <p className="py-6 text-center text-sm text-gray-500">No activity matches these filters.</p>}
          {data && (
            <div className="flex items-center justify-between border-t pt-4">
              <p className="text-sm text-gray-500">{data.count} events · Page {page} of {Math.max(1, Math.ceil(data.count / data.page_size))}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={!data.previous} onClick={() => setPage(page - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={!data.next} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Main Page Component ────────────────────────────────────────────────────

export function PlatformAdminOrganizationDetailPage() {
  const orgTimeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orgId = parseInt(id ?? '0', 10);
  const { data: org, isLoading, isError, refetch } = usePlatformOrganization(orgId);
  const deleteOrg = useDeleteOrganization();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const handleDeleteOrg = () => {
    deleteOrg.mutate({ id: orgId, confirmation: deleteConfirmation }, {
      onSuccess: () => {
        toast.success(`Organization "${org?.name}" has been permanently deleted.`);
        setDeleteOpen(false);
        setDeleteConfirmation('');
        navigate('/platform-admin/organizations');
      },
      onError: (error) => {
        toast.error(handleApiError(error).message);
      },
    });
  };

  const handleDeleteOpenChange = (open: boolean) => {
    setDeleteOpen(open);
    if (!open) {
      setDeleteConfirmation('');
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-5 w-5" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !org) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Link
          to="/platform-admin/organizations"
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Organizations
        </Link>
        <div className="py-12 text-center">
          <AlertTriangle className="h-10 w-10 text-orange-500 mx-auto mb-3" />
          <p className="text-gray-900 font-medium">Unable to load organization</p>
          <p className="text-sm text-gray-500 mt-1">The organization may not exist or you may not have permission.</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Breadcrumb + Header */}
      <div>
        <Link
          to="/platform-admin/organizations"
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Organizations
        </Link>
        <div className="flex items-center gap-3 mt-2">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{org.name}</h1>
          <Badge
            variant={org.is_active ? 'default' : 'secondary'}
            className={
              org.is_active
                ? 'bg-green-100 text-green-800 hover:bg-green-100'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-100'
            }
          >
            {org.is_active ? 'Active' : 'Inactive'}
          </Badge>
          {org.is_demo && (
            <Badge
              variant="outline"
              className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-50"
            >
              Demo
            </Badge>
          )}
        </div>
        <p className="text-gray-500 text-sm mt-1">
          {org.user_count} user{org.user_count !== 1 ? 's' : ''} &middot; Created{' '}
          {formatDateTimeInTimeZone(org.created_at, orgTimeZone)}
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="overview" className="gap-1.5">
              <Building2 className="h-3.5 w-3.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-1.5">
              <Users className="h-3.5 w-3.5" /> Users
            </TabsTrigger>
            <TabsTrigger value="subscription" className="gap-1.5">
              <CreditCard className="h-3.5 w-3.5" /> Subscription
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-1.5">
              <Activity className="h-3.5 w-3.5" /> Activity
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab orgId={orgId} />
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <UsersTab orgId={orgId} orgName={org.name} />
        </TabsContent>

        <TabsContent value="subscription" className="mt-4">
          <SubscriptionTab orgId={orgId} />
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <ActivityTab orgId={orgId} />
        </TabsContent>
      </Tabs>

      {org.is_demo && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-red-700">
              <Trash2 className="h-4 w-4" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Delete this demo organization</p>
                <p className="text-xs text-gray-500">Permanently remove this demo organization and all of its data. This action cannot be undone.</p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setDeleteConfirmation('');
                  setDeleteOpen(true);
                }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete Organization
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={handleDeleteOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Organization</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently deleting <strong>{org.name}</strong> removes the demo organization and all associated users, data, and subscriptions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="detail-delete-organization-confirmation">
              Type <strong>{org.name}</strong> to confirm
            </Label>
            <Input
              id="detail-delete-organization-confirmation"
              value={deleteConfirmation}
              onChange={(event) => setDeleteConfirmation(event.target.value)}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteOrg.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOrg}
              disabled={deleteOrg.isPending || deleteConfirmation !== org.name}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteOrg.isPending ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Deleting...</>
              ) : (
                'Delete Permanently'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
